import { useEffect, useState } from 'react';
import { getDateRange, getPreviousPeriod } from '../../../shared/utils/reportingPeriod';
import {
  getTotalOfficers,
  getTotalStudents,
  getTotalPayments,
  getTotalDisbursed,
} from '../services/statsService';

// Officer and student counts are current totals. Collected and disbursed follow the reporting period.
// With `compareToPrevious` and a single-month period, the month before it is fetched too.
export function useAdminStats(periods, { compareToPrevious = false } = {}) {
  const [totalUsers, setTotalUsers] = useState(null);
  const [totalStudents, setTotalStudents] = useState(null);
  const [totalPayments, setTotalPayments] = useState(null);
  const [totalDisbursed, setTotalDisbursed] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  // Periods is a fresh array every render, so depend on its serialised form instead.
  const periodsKey = JSON.stringify(periods);

  useEffect(() => {
    let mounted = true;

    Promise.allSettled([getTotalOfficers(), getTotalStudents()]).then(([users, students]) => {
      if (!mounted) return;
      setTotalUsers(users.status === 'fulfilled' ? users.value : null);
      setTotalStudents(students.status === 'fulfilled' ? students.value : null);
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const list = JSON.parse(periodsKey);
    const range = getDateRange(list);
    const previousRange =
      compareToPrevious && list.length === 1 && list[0].month
        ? getDateRange([getPreviousPeriod(list[0])])
        : null;

    Promise.allSettled([
      getTotalPayments(range),
      getTotalDisbursed(range),
      previousRange ? getTotalPayments(previousRange) : null,
      previousRange ? getTotalDisbursed(previousRange) : null,
    ]).then(([payments, disbursed, previousPayments, previousDisbursed]) => {
      if (!mounted) return;
      setTotalPayments(payments.status === 'fulfilled' ? payments.value : null);
      setTotalDisbursed(disbursed.status === 'fulfilled' ? disbursed.value : null);
      setPrevious(
        previousRange && previousPayments.status === 'fulfilled' && previousDisbursed.status === 'fulfilled'
          ? { totalPayments: previousPayments.value, totalDisbursed: previousDisbursed.value }
          : null
      );
      setUpdatedAt(new Date());
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [periodsKey, compareToPrevious]);

  return { totalUsers, totalStudents, totalPayments, totalDisbursed, previous, loading, updatedAt };
}
